import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-[rgba(163,74,107,0.22)] bg-white px-4 py-3 text-sm text-cream placeholder:text-mutedlilac outline-none transition-colors duration-300 focus:border-[#A34A6B]/60";

export const WaitlistForm = ({ idPrefix }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", email: "", gain: "", consent: false });
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Please add your first name and email address.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm you'd like to reserve your place.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      toast.success("Your place is reserved.");
      navigate(`/experience?name=${encodeURIComponent(data.first_name)}&ref=${data.id.slice(0, 8).toUpperCase()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong, please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-testid={`${idPrefix}-form`} onSubmit={submit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-first-name`} className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-lilac">
            First name
          </label>
          <input
            id={`${idPrefix}-first-name`}
            data-testid={`${idPrefix}-first-name-input`}
            className={inputCls}
            placeholder="Your first name"
            value={form.first_name}
            onChange={(e) => set("first_name", e.target.value)}
            autoComplete="given-name"
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-email`} className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-lilac">
            Email address
          </label>
          <input
            id={`${idPrefix}-email`}
            data-testid={`${idPrefix}-email-input`}
            type="email"
            className={inputCls}
            placeholder="you@example.com"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            autoComplete="email"
          />
        </div>
      </div>
      <div>
        <label htmlFor={`${idPrefix}-gain`} className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-lilac">
          What would you most like to gain from the retreat? <span className="normal-case text-mutedlilac">(optional)</span>
        </label>
        <textarea
          id={`${idPrefix}-gain`}
          data-testid={`${idPrefix}-gain-input`}
          rows={2}
          className={`${inputCls} resize-none`}
          placeholder="Share as much or as little as you like…"
          value={form.gain}
          onChange={(e) => set("gain", e.target.value)}
        />
      </div>
      <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-lilac">
        <Checkbox
          data-testid={`${idPrefix}-consent-checkbox`}
          checked={form.consent}
          onCheckedChange={(v) => set("consent", v === true)}
          className="mt-0.5 border-[#A34A6B]/40 data-[state=checked]:bg-[#A34A6B] data-[state=checked]:text-white"
        />
        <span>
          I'd like to reserve my place and have read the privacy notice. I understand I'll receive a
          confirmation and essential updates about this retreat. <span className="text-[#A34A6B]">*</span>
        </span>
      </label>
      <button
        type="submit"
        data-testid={`${idPrefix}-submit-button`}
        disabled={submitting}
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#A34A6B] px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-[#FDF6F2] transition-[background-color,transform,box-shadow] duration-300 hover:bg-[#8E3D5B] hover:shadow-[0_0_36px_rgba(163,74,107,0.3)] active:scale-[0.98] disabled:opacity-60"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Find out more information
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      <p className="text-center text-[11px] leading-relaxed text-mutedlilac">
        Venue and final inclusions to be confirmed · Joining is not a booking commitment · Your details are
        stored securely and never sold.
      </p>
    </form>
  );
};
